import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useSelector } from "react-redux";

import Sidebar from "../../components/sidebar/Sidebar";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import KpiCard from "../../components/commercial-dashboard/KpiCard";
import CostAnalysisControls from "../../components/cost-analysis/CostAnalysisControls";
import CostAnalysisTable from "../../components/cost-analysis/CostAnalysisTable";
import CostAnalysisLines from "../../components/cost-analysis/CostAnalysisLines";
import {
  monthLabel,
  money,
  monthOffset,
  pct,
  unitMoney,
  valueLabel,
} from "../../components/cost-analysis/costAnalysisFormat";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUser } from "../../store/slices/session/sessionSelector";
import { useUserRole } from "../../hooks/useUserRole";
import {
  CostAnalysisGroup,
  CostAnalysisParams,
  CostDimension,
  NONE_VALUE,
  toCostAnalysisQuery,
  useGetCostAnalysisQuery,
} from "../../store/apis/costAnalysisApi";

const INITIAL_PARAMS: CostAnalysisParams = {
  from: monthOffset(5),
  to: monthOffset(0),
  groupBy: ["platform", "campaignType", "objective"],
  filters: {},
  includeUnmargined: false,
  targetMargin: null,
};

/** Quién la ve: tiene costes reales de plataforma. */
export const COST_ANALYSIS_ROLES = ["ADMIN", "PRICING", "STRATEGY"];

/**
 * Análisis de costes: coste real de plataforma frente a venta de todas las líneas de
 * briefing con datos, agrupado por plataforma, tipo de campaña, objetivo, sector, tipo de
 * cliente... Para saber cuánto cuesta de verdad cada cosa y ajustar el margen.
 */
const CostAnalysis: React.FC = () => {
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const user = useSelector(selectUser);
  const { role } = useUserRole();
  const canSee = !!role && COST_ANALYSIS_ROLES.includes(role);

  const [params, setParams] = useState<CostAnalysisParams>(INITIAL_PARAMS);
  const [showDelivery, setShowDelivery] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Grupo abierto con "Ver líneas": qué se está viendo y los filtros a los que volver.
  const [openGroup, setOpenGroup] = useState<{
    label: string;
    previousFilters: CostAnalysisParams["filters"];
  } | null>(null);
  const linesRef = useRef<HTMLDivElement>(null);

  /** Cambios desde los controles: dejan de ser "el grupo abierto". */
  const changeParams = (next: CostAnalysisParams) => {
    setOpenGroup(null);
    setParams(next);
  };

  /** Filtra por los valores del grupo, para que la lista de líneas sea la suya. */
  const showGroupLines = (group: CostAnalysisGroup) => {
    if (!view) return;
    const dims = view.groupBy as CostDimension[];
    const companyNames = new Map(view.companies.map((c) => [c.id, c.name]));
    const filters = { ...params.filters };
    dims.forEach((d) => {
      filters[d] = [group.key[d] ?? NONE_VALUE];
    });
    setOpenGroup({
      label: dims
        .map((d) => valueLabel(d, group.key[d], companyNames))
        .join(" · "),
      previousFilters: openGroup?.previousFilters ?? params.filters,
    });
    setParams({ ...params, filters });
    linesRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const closeGroup = () => {
    if (openGroup) setParams({ ...params, filters: openGroup.previousFilters });
    setOpenGroup(null);
  };

  const { data, isLoading, isFetching, error } = useGetCostAnalysisQuery(
    params,
    {
      skip: !canSee,
    }
  );
  const view = data?.data ?? undefined;
  const totals = view?.totals;
  const mixedObjectives =
    !!view &&
    view.groups.some((g) => g.objective === null) &&
    view.groups.length > 0;
  const currencies = totals?.currencies ?? [];
  // CPM, CPC... cuando todo lo filtrado se vende igual; si no, no hay precio por unidad que dar.
  const unit = totals?.objective ?? null;

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BACKEND_URL}/api/cost-analysis/export?${toCostAnalysisQuery(params)}`,
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Analisis_Costes_${params.from}_${params.to}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setExportError(
        "No se ha podido descargar el Excel. Vuelve a intentarlo."
      );
    } finally {
      setExporting(false);
    }
  };

  const errorStatus =
    error && "status" in error && typeof error.status === "number"
      ? error.status
      : null;
  const errorMessage =
    error && "data" in error
      ? ((error.data as { message?: string } | undefined)?.message ?? null)
      : null;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        {!canSee ? (
          <Alert severity="info">
            El análisis de costes solo está disponible para Admin, Pricing y
            Strategy.
          </Alert>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[200]}`,
                p: { xs: 2, md: 3 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={1}
              >
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="h5">Análisis de costes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Coste real de plataforma frente a venta de las líneas de
                    briefing, para ajustar el margen por plataforma, tipo de
                    campaña, objetivo y cliente.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    exporting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleExport}
                  disabled={exporting || !view}
                >
                  Descargar Excel
                </Button>
              </Stack>
              <CostAnalysisControls
                value={params}
                onChange={changeParams}
                view={view}
              />
            </Paper>

            {exportError && (
              <Alert severity="error" onClose={() => setExportError(null)}>
                {exportError}
              </Alert>
            )}

            {isLoading && (
              <Stack alignItems="center" py={6} gap={1}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Leyendo las métricas mes a mes…
                </Typography>
              </Stack>
            )}

            {error && (
              <Alert severity="error">
                {errorStatus === 403
                  ? "No tienes permiso para ver el análisis de costes."
                  : (errorMessage ??
                    "No se ha podido cargar el análisis de costes.")}
              </Alert>
            )}

            {view && totals && (
              <>
                {openGroup && (
                  <Alert
                    severity="info"
                    action={
                      <Button color="inherit" size="small" onClick={closeGroup}>
                        Volver a todos
                      </Button>
                    }
                  >
                    Viendo solo {openGroup.label}: cifras y líneas de ese grupo.
                  </Alert>
                )}
                {/* Lo que se busca aquí: a cuánto se compra, a cuánto se vende y qué margen se pone */}
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr 1fr",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                  }}
                >
                  <KpiCard
                    label={unit ? `${unit} de compra` : "Compra por unidad"}
                    value={unitMoney(totals.realUnitCost)}
                    caption={
                      !unit
                        ? "Filtra por un objetivo para verlo"
                        : totals.plannedNetUnitCost !== null
                          ? `Plan ${unitMoney(totals.plannedNetUnitCost)}`
                          : undefined
                    }
                  />
                  <KpiCard
                    label={unit ? `${unit} de venta` : "Venta por unidad"}
                    value={unitMoney(totals.saleUnitCost)}
                    caption={
                      !unit
                        ? "Filtra por un objetivo para verlo"
                        : totals.plannedGrossUnitCost !== null
                          ? `Briefing ${unitMoney(totals.plannedGrossUnitCost)}`
                          : undefined
                    }
                  />
                  <KpiCard
                    label="Margen real"
                    value={pct(totals.marginPercent)}
                    caption={
                      totals.plannedMarginPercent !== null
                        ? `Plan ${pct(totals.plannedMarginPercent)}`
                        : undefined
                    }
                    tone={
                      totals.marginPercent !== null &&
                      totals.plannedMarginPercent !== null &&
                      totals.marginPercent < totals.plannedMarginPercent - 2
                        ? "warning"
                        : undefined
                    }
                  />
                  <KpiCard
                    label="Margen que se pone"
                    value={pct(totals.lineMarginMedian)}
                    caption={
                      totals.lineMarginP25 !== null &&
                      totals.lineMarginP75 !== null &&
                      totals.lineMarginP25 !== totals.lineMarginP75
                        ? `La mitad de las líneas, entre ${pct(totals.lineMarginP25, 0)} y ${pct(totals.lineMarginP75, 0)}`
                        : "Mediana del margen de las líneas"
                    }
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr 1fr",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                  }}
                >
                  <KpiCard
                    label="Coste real"
                    value={money(totals.realCost)}
                    caption={`${totals.lines} líneas · ${totals.clients} clientes`}
                  />
                  <KpiCard label="Venta" value={money(totals.sale)} />
                  <KpiCard
                    label="Beneficio"
                    value={money(totals.grossProfit)}
                  />
                </Box>

                <Stack gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    {monthLabel(view.from)} – {monthLabel(view.to)}, con datos
                    hasta el{" "}
                    {new Date(view.dataUntil).toLocaleDateString("es-ES")}.
                    {view.excludedUnmarginedLines > 0 &&
                      ` Fuera ${view.excludedUnmarginedLines} líneas de paid media que paga el cliente (sin margen).`}
                  </Typography>
                  {mixedObjectives && (
                    <Alert severity="info">
                      Hay grupos que mezclan objetivos (CPM con CPC, por
                      ejemplo) y no tienen coste unitario. Agrupa o filtra por
                      objetivo para verlo.
                    </Alert>
                  )}
                  {currencies.length > 1 && (
                    <Alert severity="warning">
                      Hay líneas en varias monedas ({currencies.join(", ")}) y
                      los importes se suman sin convertir. Filtra por cliente o
                      plataforma para separarlas.
                    </Alert>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {view.groups.length}{" "}
                    {view.groups.length === 1 ? "grupo" : "grupos"}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showDelivery}
                        onChange={(e) => setShowDelivery(e.target.checked)}
                      />
                    }
                    label="CPM, CPC, CPV, CPA, CTR y VTR"
                  />
                </Stack>
                <Box sx={{ position: "relative" }}>
                  {isFetching && (
                    <LinearProgress
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 3,
                      }}
                    />
                  )}
                  {view.groups.length === 0 ? (
                    <Alert severity="info">
                      No hay líneas con actividad para este periodo y estos
                      filtros.
                    </Alert>
                  ) : (
                    <CostAnalysisTable
                      view={view}
                      showDelivery={showDelivery}
                      onShowLines={showGroupLines}
                    />
                  )}
                </Box>

                {/* `?? []`: un backend anterior a las líneas no las manda */}
                {(view.lines ?? []).length > 0 && (
                  <Box ref={linesRef} sx={{ scrollMarginTop: 16 }}>
                    <CostAnalysisLines lines={view.lines} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CostAnalysis;
